import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import Button from "@elements/button";
import LeftArrow from "@svg/LeftArrow";
import Loading from "assets/lottie/loading";
import ramadanRiddlesApi from "@api/ramadanRiddlesApi";
import { useAppContext } from "@context/app";
import { segmentEvent } from "@utils/segment";
import {
    useRiddlesActions,
    useRiddlesNavigation,
    useRiddlesOverview,
    useRiddlesQuestion,
    useRiddlesSubmitResult,
} from "../RamadanRiddlesContext";

const RamadanRiddlesQuestion: React.FC = () => {
    const t = useTranslations();
    const { locale } = useRouter();
    const { setView } = useRiddlesNavigation();
    const { data: questionData, isLoading: isQuestionLoading } = useRiddlesQuestion();
    const { data: overviewData } = useRiddlesOverview();
    const { user, country, area, eventProperties } = useAppContext();

    const { isLoading: isSubmitting } = useRiddlesSubmitResult();
    const {
        setQuestion,
        setQuestionError,
        setQuestionLoading,
        setSubmitResult,
        setSubmitResultError,
        setSubmitResultLoading,
        setOverview,
    } = useRiddlesActions();
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
    const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
    const [showTryAgain, setShowTryAgain] = useState(false);
    const isFetchingRef = useRef(false);
    const isSubmittingRef = useRef(false);
    const hasTrackedViewRef = useRef(false);

    const options = questionData?.answers_option ?? [];

    const headerTitle = questionData?.title ?? t("Day of Ramadan");
    const questionTitle = questionData?.question_title ?? t("Today's question will appear here");
    const attemptsLeft = remainingAttempts ?? questionData?.number_of_attemps ?? 0;
    const currentDayNumber = overviewData?.ramadan_days?.find(
        (day) => day.status === "Current"
    )?.day_number;
    const language = locale?.includes("en") ? "English" : "Arabic";

    const refreshOverview = useCallback(async () => {
        const overviewResponse = await ramadanRiddlesApi.getOverview().fetch();
        if (overviewResponse?.meta?.code === 200) {
            setOverview(overviewResponse.data);
        }
    }, [setOverview]);

    useEffect(() => {
        let isActive = true;

        const fetchQuestion = async () => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            setQuestionError(null);
            setQuestionLoading(true);

            try {
                const response = await ramadanRiddlesApi.getQuestion().fetch();
                if (!isActive) return;

                if (response?.meta?.code === 200) {
                    setQuestion(response.data);
                } else {
                    setQuestionError(response?.meta?.msg || t("client_general_error"));
                    toast.error(response?.meta?.msg || t("client_general_error"));
                }
            } catch (error) {
                console.error("Failed to load Ramadan riddle question:", error);
                if (isActive) {
                    setQuestionError(t("client_general_error"));
                    toast.error(t("client_general_error"));
                }
            } finally {
                if (isActive) {
                    setQuestionLoading(false);
                }
                isFetchingRef.current = false;
            }
        };

        fetchQuestion();

        return () => {
            isActive = false;
        };
    }, [setQuestion, setQuestionError, setQuestionLoading, t]);

    useEffect(() => {
        if (hasTrackedViewRef.current) return;
        hasTrackedViewRef.current = true;

        segmentEvent("Ramadan Question Page Opened", {
            ...eventProperties,
            area_id: area?.id,
            area_slug: area?.slug,
            country_id: country?.id,
            country_slug: country?.slug,
            language,
            user_id: user?.data?.id || -1,
        });
    }, [
        area?.id,
        area?.slug,
        country?.id,
        country?.slug,
        eventProperties,
        language,
        user?.data?.id,
    ]);

    useEffect(() => {
        setSelectedOptionId(null);
        setRemainingAttempts(null);
        setShowTryAgain(false);
    }, [questionData?.id]);

    const handleSubmit = async () => {
        if (!selectedOptionId || !questionData?.id || isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setSubmitResultError(null);
        setSubmitResultLoading(true);
        const selectedOption = options.find((option) => option.id === selectedOptionId);

        try {
            const response = await ramadanRiddlesApi
                .submitQuestion({
                    question_id: questionData.id,
                    answer_id: selectedOptionId,
                })
                .fetch();

            if (response?.meta?.code === 200) {
                setSubmitResult(response.data);
                const isCorrect = (response.data?.rewards?.length ?? 0) > 0;
                const remainingAttemptsValue =
                    response.data?.number_of_remaining_attempts ?? attemptsLeft;
                const dayNumberValue = currentDayNumber ? Number(currentDayNumber) : null;
                const dayNumberPayload =
                    dayNumberValue !== null && !Number.isNaN(dayNumberValue)
                        ? dayNumberValue
                        : currentDayNumber ?? "";

                segmentEvent("Ramadan Riddle Question Answered", {
                    ...eventProperties,
                    area_id: area?.id,
                    area_slug: area?.slug,
                    country_id: country?.id,
                    country_slug: country?.slug,
                    language,
                    user_id: user?.data?.id || -1,
                    answer_id: selectedOptionId,
                    answer_text: selectedOption?.title ?? "",
                    day_number: dayNumberPayload,
                    is_correct: isCorrect,
                    number_of_remaining_attempts: String(remainingAttemptsValue ?? 0),
                    question_id: questionData?.id ?? "",
                    question_text: questionData?.question_title ?? "",
                });

                if ((response.data?.rewards?.length ?? 0) > 0) {
                    await refreshOverview();
                    setView("correct-answer");
                } else {
                    setSelectedOptionId(null);
                    setRemainingAttempts(response.data?.number_of_remaining_attempts ?? 0);
                    setShowTryAgain(true);
                    if (response.data?.number_of_remaining_attempts === 0) {
                        toast.error(t("You have used all your attempts for today"));
                        await refreshOverview();
                        setView("progress");
                    }
                }
            } else {
                setSubmitResultError(response?.meta?.msg || t("client_general_error"));
                toast.error(response?.meta?.msg || t("client_general_error"));
            }
        } catch (error) {
            console.error("Failed to submit Ramadan riddle answer:", error);
            setSubmitResultError(t("client_general_error"));
            toast.error(t("client_general_error"));
        } finally {
            setSubmitResultLoading(false);
            isSubmittingRef.current = false;
        }
    };

    return (
        <div className="flex h-full flex-col bg-[#FDDBA6]">
            <div className="relative flex min-h-screen flex-1 flex-col">
                <div className="relative">
                    <Image
                        src="/svgs/ramadan-riddle-misbah-header.svg"
                        alt=""
                        aria-hidden="true"
                        width={419}
                        height={179}
                        sizes="100vw"
                        className="mx-auto h-auto w-[85%]"
                    />
                    <button
                        type="button"
                        onClick={() => setView("progress")}
                        className="absolute left-5 top-4 text-[#38190D]"
                        aria-label={t("Back")}
                    >
                        <LeftArrow />
                    </button>
                </div>
                {isQuestionLoading ? (
                    <Loading isContained />
                ) : (
                    <div className="px-5 pb-6">
                        <div className="mt-2 text-center">
                            <p className="text-sm font-semibold text-[#38190D]">
                                {t("Today's Riddle")}
                            </p>
                            <h1 className="mt-1 text-2xl font-extrabold uppercase text-[#38190D]">
                                {headerTitle}
                            </h1>
                            <p className="mt-4 text-base font-medium text-[#38190D]">
                                {questionTitle}
                            </p>
                        </div>

                        <div className="mt-6 flex flex-col gap-4">
                            {options.map((option) => {
                                const isSelected = selectedOptionId === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => setSelectedOptionId(option.id)}
                                        className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left text-sm font-semibold transition-colors ${
                                            isSelected
                                                ? "border-[#F7A658] bg-[#F7A658] text-[#38190D]"
                                                : "border-[#F2E3C9] bg-white text-[#38190D]"
                                        }`}
                                    >
                                        <span
                                            className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                                isSelected
                                                    ? "border-[#38190D] bg-white/30"
                                                    : "border-[#C9B39F]"
                                            }`}
                                            aria-hidden="true"
                                        >
                                            {isSelected ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    className="h-3 w-3 text-[#38190D]"
                                                >
                                                    <path
                                                        d="M4 8.5l2.2 2.2L12 5"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            ) : null}
                                        </span>
                                        <span>{option.title}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <Button
                            className="mt-7 h-12 w-full rounded-full bg-[#F27D23] font-bold uppercase text-white outline-none border-none"
                            isDisabled={!selectedOptionId || isSubmitting}
                            onClick={handleSubmit}
                        >
                            {t("Submit answer")}
                        </Button>

                        <p
                            className={`mt-6 text-center text-sm font-medium ${
                                showTryAgain ? "text-[#E23C3C]" : "text-[#956C4A]"
                            }`}
                        >
                            {showTryAgain ? `${t("Try again")}: ` : ""}
                            {t("attemptsMessage", { count: attemptsLeft })}
                        </p>
                    </div>
                )}
                <div className="pt-6 mt-auto">
                    <Image
                        src="/svgs/ramadan-riddle-cloud-mosque.svg"
                        alt=""
                        aria-hidden="true"
                        width={498}
                        height={313}
                        sizes="100vw"
                        className="h-auto w-full"
                    />
                </div>
            </div>
        </div>
    );
};

export default RamadanRiddlesQuestion;
